import { useEffect } from 'react';

const BRAND = 'SLL Community Portal | Sustainable Logistics Network';

// In-memory tracker so we restore the original site-wide title on unmount
// without leaving stale per-page titles in the tab when navigating away
// before the next page mounts and sets its own title.
let originalTitle = null;
if (typeof document !== 'undefined') originalTitle = document.title;

function setMeta(selector, attr, value) {
  if (typeof document === 'undefined' || !value) return;
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

/**
 * usePageMeta — sets the browser tab title plus the og:/twitter: meta tags
 * for the current page. When sharing a link, this is what most browsers and
 * a subset of social platforms (notably anything that runs the page through
 * a headless browser, like LinkedIn's newer crawler) pick up.
 *
 * NOTE: classic social crawlers (facebookexternalhit, Slackbot) only read
 * the static <head> and won't see these client-side updates — for those a
 * server-side share endpoint is required.
 *
 * @param {object} opts
 * @param {string} opts.title    Required. The specific page/article title.
 * @param {string} [opts.description] Optional, used for og/twitter description.
 * @param {string} [opts.image]  Optional og:image override.
 * @param {string} [opts.url]    Optional canonical URL; defaults to current location.
 */
export default function usePageMeta({ title, description, image, url } = {}) {
  useEffect(() => {
    if (!title) return;
    const full = `${title} · ${BRAND}`;
    const prevTitle = document.title;
    document.title = full;
    setMeta("meta[name='title']", 'content', full);
    setMeta("meta[property='og:title']", 'content', full);
    setMeta("meta[property='twitter:title']", 'content', full);
    if (description) {
      setMeta("meta[name='description']", 'content', description);
      setMeta("meta[property='og:description']", 'content', description);
      setMeta("meta[property='twitter:description']", 'content', description);
    }
    if (image) {
      setMeta("meta[property='og:image']", 'content', image);
      setMeta("meta[property='twitter:image']", 'content', image);
    }
    const canonical = url || (typeof window !== 'undefined' ? window.location.href : '');
    if (canonical) {
      setMeta("meta[property='og:url']", 'content', canonical);
      setMeta("meta[property='twitter:url']", 'content', canonical);
    }
    return () => {
      document.title = prevTitle || originalTitle || BRAND;
    };
  }, [title, description, image, url]);
}
